/**
 * Judgement bot.
 *
 * - Bidding: deals the unseen cards to the opponents at random many times, plays each deal out with a simple
 *   "win when you can" policy, and bids whatever maximises the expected round score given how many tricks the hand
 *   took in those trials (so it respects the miss penalty and the dealer's hook).
 * - Play: aims for the exact bid. Short of its bid it wins tricks as cheaply as it can; at (or past) its bid it ducks,
 *   dumping its highest losing cards and never trumping in when it would take an unwanted trick.
 * The bot only ever reads its own seat's view.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { SeededRng, shuffle, type Rng } from '../../core/rng.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  judgementScore,
  judgementTrickWinner,
  type JudgementAction,
  type JudgementLegal,
  type JudgementTrickPlay,
  type JudgementView,
} from './engine.js';

const SEATS = 4;
const rank = (c: CardId) => parseCard(c).rank;
const suit = (c: CardId) => parseCard(c).suit;
const byRank = (a: CardId, b: CardId) => rank(a) - rank(b);

/** Card strength with trumps above everything else. */
const strength = (c: CardId, trump: string) => rank(c) + (suit(c) === trump ? 20 : 0);

function legalFor(hand: readonly CardId[], trick: readonly JudgementTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = suit(trick[0]!.card);
  const followers = hand.filter((c) => suit(c) === lead);
  return followers.length > 0 ? followers : [...hand];
}

/** Does `card` currently beat everything on the table? */
function wouldWin(card: CardId, trick: readonly JudgementTrickPlay[], trump: string, seat: number): boolean {
  return judgementTrickWinner([...trick, { seat, card }], trump) === seat;
}

/** The "win it if you can, as cheaply as you can" policy used inside bid simulations. */
function greedyCard(hand: readonly CardId[], trick: readonly JudgementTrickPlay[], trump: string, seat: number): CardId {
  const legal = legalFor(hand, trick);
  if (trick.length === 0) {
    const plain = legal.filter((c) => suit(c) !== trump).sort(byRank);
    if (plain.length > 0 && rank(plain.at(-1)!) >= 12) return plain.at(-1)!;
    const trumps = legal.filter((c) => suit(c) === trump).sort(byRank);
    if (trumps.length > 0) return trumps.at(-1)!;
    return plain.at(-1)!;
  }
  const winners = legal.filter((c) => wouldWin(c, trick, trump, seat)).sort((a, b) => strength(a, trump) - strength(b, trump));
  if (winners.length > 0) return winners[0]!;
  return [...legal].sort((a, b) => strength(a, trump) - strength(b, trump))[0]!;
}

/** Plays a whole round with every seat using the greedy policy and returns the tricks `me` took. */
function simulateTricks(hands: CardId[][], me: number, leader: number, trump: string): number {
  const hs = hands.map((h) => [...h]);
  const rounds = hs[0]!.length;
  let lead = leader;
  let won = 0;
  for (let t = 0; t < rounds; t++) {
    const trick: JudgementTrickPlay[] = [];
    for (let i = 0; i < SEATS; i++) {
      const seat = (lead + i) % SEATS;
      const card = greedyCard(hs[seat]!, trick, trump, seat);
      hs[seat]!.splice(hs[seat]!.indexOf(card), 1);
      trick.push({ seat, card });
    }
    lead = judgementTrickWinner(trick, trump);
    if (lead === me) won++;
  }
  return won;
}

/** Distribution (index = tricks won) of how many tricks this hand takes over `samples` random deals. */
export function judgementTrickDistribution(hand: readonly CardId[], turnCard: CardId, seat: number, dealer: number, rng: Rng, samples: number): number[] {
  const n = hand.length;
  const unseen = buildDeck().filter((c) => c !== turnCard && !hand.includes(c));
  const trump = suit(turnCard);
  const leader = (dealer + 1) % SEATS;
  const counts = Array<number>(n + 1).fill(0);
  for (let s = 0; s < samples; s++) {
    const deck = shuffle([...unseen], rng);
    const hands: CardId[][] = [];
    let k = 0;
    for (let p = 0; p < SEATS; p++) {
      if (p === seat) hands.push([...hand]);
      else hands.push(deck.slice(k, (k += n)));
    }
    counts[simulateTricks(hands, seat, leader, trump)]!++;
  }
  return counts.map((c) => c / samples);
}

/** Expected tricks for a hand (mean of the simulated distribution). */
export function estimateJudgementTricks(hand: readonly CardId[], turnCard: CardId, seat: number, dealer: number, rng: Rng, samples = 80): number {
  return judgementTrickDistribution(hand, turnCard, seat, dealer, rng, samples).reduce((a, p, k) => a + p * k, 0);
}

/** Bid maximising expected score over the simulated distribution, skipping the dealer's forbidden number. */
export function chooseJudgementBid(view: JudgementView, legal: Extract<JudgementLegal, { kind: 'bid' }>, rng: Rng, samples: number): number {
  const seat = view.seat ?? 0;
  const dist = judgementTrickDistribution(view.hand, view.turnCard, seat, view.dealer, rng, samples);
  let best = -1;
  let bestEv = -Infinity;
  for (let b = legal.min; b <= legal.max; b++) {
    if (b === legal.forbidden) continue;
    const ev = dist.reduce((a, p, k) => a + p * judgementScore(b, k, view.config.penalizeMiss), 0);
    if (ev > bestEv + 1e-9) {
      bestEv = ev;
      best = b;
    }
  }
  return best;
}

/** A deterministic suggestion for the bid picker (same hand → same suggestion). */
export function suggestJudgementBid(view: JudgementView, legal: Extract<JudgementLegal, { kind: 'bid' }>): number {
  let h = 17;
  for (const ch of view.hand.join('') + view.turnCard) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return chooseJudgementBid(view, legal, new SeededRng(h), 60);
}

function unseenCards(view: JudgementView, ctx: BotContext): Set<CardId> {
  const seen = new Set<CardId>(view.hand);
  seen.add(view.turnCard);
  view.trick.forEach((p) => seen.add(p.card));
  const n = view.tricks.length;
  view.tricks.forEach((t, i) => {
    if (ctx.profile.memory >= 1 || i >= n - Math.max(1, Math.round(n * ctx.profile.memory))) for (const p of t.plays) seen.add(p.card);
  });
  return new Set(buildDeck().filter((c) => !seen.has(c)));
}

function chooseCard(view: JudgementView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  const me = view.seat ?? 0;
  const trump = view.trump;
  const trick = view.trick;
  const bid = view.bids[me] ?? 0;
  const need = bid - (view.tricksWon[me] ?? 0);
  const left = view.cards - view.tricks.length;
  const unseen = ctx.profile.memory > 0 ? unseenCards(view, ctx) : null;
  const asc = [...legal].sort((a, b) => strength(a, trump) - strength(b, trump));

  if (need > 0) {
    // Short of the bid: win cheaply when possible, otherwise throw the weakest card.
    if (trick.length === 0) {
      const plain = legal.filter((c) => suit(c) !== trump).sort(byRank);
      // A plain ace/king (or a card nothing unseen can beat) is the best lead.
      const master = plain.filter((c) => (unseen ? ![...unseen].some((u) => suit(u) === suit(c) && rank(u) > rank(c)) : rank(c) >= 13));
      if (master.length > 0) return master.at(-1)!;
      const trumps = legal.filter((c) => suit(c) === trump).sort(byRank);
      if (trumps.length > 0 && need >= left) return trumps.at(-1)!;
      if (plain.length > 0) return plain.at(-1)!;
      return trumps.at(-1)!;
    }
    const winners = asc.filter((c) => wouldWin(c, trick, trump, me));
    if (winners.length > 0) {
      // Last to play: the cheapest winner is certain. Otherwise a higher card guards against being overtaken.
      if (trick.length === SEATS - 1) return winners[0]!;
      return winners[Math.min(winners.length - 1, winners.length > 1 && need >= left ? 1 : 0)]!;
    }
    return asc[0]!;
  }

  // At or past the bid: avoid taking this trick.
  if (trick.length === 0) {
    const plain = legal.filter((c) => suit(c) !== trump).sort(byRank);
    return plain.length > 0 ? plain[0]! : asc[0]!;
  }
  const losers = asc.filter((c) => !wouldWin(c, trick, trump, me));
  if (losers.length > 0) {
    // Dump the highest card that still loses, preferring plain cards over trumps.
    const plainLosers = losers.filter((c) => suit(c) !== trump);
    return (plainLosers.length > 0 ? plainLosers : losers).at(-1)!;
  }
  // Every legal card wins: take it with the weakest.
  return asc[0]!;
}

export const judgementBot: BotEngine<JudgementView, JudgementLegal, JudgementAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    const samples = ctx.profile.simulations > 0 ? ctx.profile.simulations : 6;
    let value = chooseJudgementBid(view, legal, ctx.rng, samples);
    if (chance(ctx, ctx.profile.mistakeRate)) {
      const options = Array.from({ length: legal.max - legal.min + 1 }, (_, i) => legal.min + i).filter((b) => b !== legal.forbidden && Math.abs(b - value) <= 1);
      value = options[ctx.rng.int(options.length)] ?? value;
    }
    return { type: 'bid', value };
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const others = view.totals.filter((_, s) => s !== me);
    const avg = others.reduce((a, b) => a + b, 0) / others.length;
    return Math.max(0, Math.min(1, 0.5 + (view.totals[me]! - avg) / 150));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') return legal.max === 0 ? 0 : 0.5;
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return { type: 'bid', value: legal.forbidden === 0 ? 1 : 0 };
    if (legal.kind === 'play') return { type: 'play', card: [...legal.cards].sort(byRank)[0]! };
    throw new Error('No legal action');
  },
};
